// src/components/SharePage.tsx
"use client";

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import EmissaryOptimized from "@/components/EmissaryOptimized";
import { User } from 'firebase/auth';
import FirebaseService from '@/lib/firebase'; // Keep FirebaseService import
import LoginDialog from "@/components/LoginDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Shield, LogIn, Home, Plus, Copy, GitBranch } from "lucide-react";
import Link from 'next/link';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { checkAccess, getAccessDeniedMessage } from '@/lib/access-control';
import { createDuplicateConfig } from '@/lib/admin-utils';
import type { ConfigProps, Document } from '@/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import Aurora from '@/components/Aurora';
import { SharingStatus } from '@/components/SharingStatus';
import { useToast } from '@/components/hooks/use-toast';
import ArtifactToggleButton from '@/components/ArtifactToggleButton';
import ChatWithArtifacts from '@/components/ChatWithArtifacts';
import { ArtifactProvider } from '@/contexts/ArtifactContext';
import type { AccessControl } from '@/lib/firebase';
import { ParentChatLink } from '@/components/ParentChatLink';
import { AssistantSwitcher } from '@/components/AssistantSwitcher';


interface SharePageProps {
  config: ConfigProps;
}

export default function SharePage({ config }: SharePageProps) {
  const { toast } = useToast();
  const [userState, setUserState] = useState<'not-logged-in' | 'receiver' | 'admin'>('not-logged-in');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false); 
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null); // Start as null to indicate loading/uncertainty
  const hasIncrementedUsageRef = useRef(false);
  const [localAccessControl, setLocalAccessControl] = useState<AccessControl | undefined>(undefined);

  const {
    // Use correct names matching ConfigProps / Assistant
    name,               // Renamed from botName
    avatar,             // Renamed from botAvatar
    assistantId,        // Renamed from shareId (assuming shareId was assistantId)
    accessControl,      // Renamed from access
    adminEmail,         // Keep if correct
    senderName,         // Keep if correct
    initialMessage,     // Keep if correct
    initialInstructions,// Keep if correct
    initialDocuments,   // Keep if correct
    tools,              // Keep if correct
    toolConfigs,        // Keep if correct
    templateId,         // Destructure if needed by child components
    parentAssistantId   // NEW: For provenance tracking
  } = useMemo(() => config, [config]);

  // Initialize local access control state from props
  useEffect(() => {
    if (accessControl && !localAccessControl) {
      setLocalAccessControl(accessControl);
    }
  }, [accessControl, localAccessControl]);

  // Auth listener effect
  useEffect(() => {
    //console.log("Setting up Firebase auth listener...");
    const unsubscribe = FirebaseService.onAuthStateChange((user) => {
      //console.log("Firebase onAuthStateChange triggered. User:", user ? user.email : 'null');
      setCurrentUser(user);
      setIsAuthInitialized(true); // <-- Mark initial auth check as complete
      console.log("Authentication initialized.");
    });
    // Cleanup function
    return () => {
      //console.log("Cleaning up Firebase auth listener.");
      unsubscribe();
    }
  }, []); // Empty dependency array ensures this runs only once on mount

  // Access check effect
  useEffect(() => {
    // Wait until BOTH initial auth check is done AND accessControl is available
    if (isAuthInitialized && typeof localAccessControl !== 'undefined') {
      //console.log("Auth initialized and config ready. Checking access...");
      //console.log("Checking access with:", { accessControl, currentUser, adminEmail });

      const accessGranted = checkAccess(localAccessControl, currentUser, adminEmail);
      setHasAccess(accessGranted); // <-- Set the final access state
      //console.log("Access determined:", accessGranted);

      // Increment usage only once when access is first granted
      if (accessGranted && !hasIncrementedUsageRef.current && assistantId) {
        FirebaseService.incrementAssistantUsage(assistantId)
          .then(() => {
            hasIncrementedUsageRef.current = true;
            //console.log(`Usage incremented for assistant: ${assistantId}`);
          })
          .catch((error) => {
            console.warn(`Non-critical error incrementing usage for assistant ${assistantId}:`, error);
          });
      }
    } else {
      // Still waiting for auth init or config... Keep showing loading state.
      console.log("Access check waiting:", { isAuthInitialized, isAccessControlDefined: typeof localAccessControl !== 'undefined' });
      // Ensure we stay in loading state if conditions aren't met
      setHasAccess(null); 
    }

    // Set user state based on login status and ownership (can run based on currentUser changes)
    if (currentUser) {
      setUserState(currentUser.email === adminEmail ? 'admin' : 'receiver');
    } else {
      setUserState('not-logged-in');
    }
    // Add isAuthInitialized to dependencies
  }, [currentUser, adminEmail, localAccessControl, assistantId, isAuthInitialized]); 


  // Auth listener remains the same
  useEffect(() => {
    const unsubscribe = FirebaseService.onAuthStateChange((user) => {
      setCurrentUser(user);
      // Reset usage ref if user logs out/changes (optional, depends on desired logic)
      // if (!user) {
      //   hasIncrementedUsageRef.current = false;
      // }
    });
    return () => unsubscribe();
  }, []);

  // Logout handler remains the same
  const handleLogout = useCallback(async () => {
    try {
      await FirebaseService.logout();
      // setUserState('not-logged-in'); // This will be handled by the auth listener effect
      setHasAccess(null); // Re-evaluate access on logout
      hasIncrementedUsageRef.current = false; // Reset usage count on logout
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  const [activeTools, setActiveTools] = useState<string[]>(tools || []);

  // Handler for updating access control
  const handleAccessChange = useCallback(async (newAccessControl: AccessControl) => {
    // Only admins can update access control
    if (userState !== 'admin' || !assistantId) {
      toast({
        title: "Not authorized",
        description: "Only the assistant's creator can change sharing settings.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Update the assistant in Firebase
      await FirebaseService.updateAssistant(assistantId, {
        accessControl: newAccessControl
      });

      // Update local state
      setLocalAccessControl(newAccessControl);

      toast({
        title: "Sharing settings updated",
        description: "Your changes have been saved successfully."
      });
    } catch (error) {
      console.error("Error updating access control:", error);
      toast({
        title: "Update failed",
        description: "There was a problem updating sharing settings. Please try again.",
        variant: "destructive"
      });
    }
  }, [userState, assistantId, toast]);

  const handleToolsChange = useCallback((updatedTools: string[]) => {
    //console.log("Tools updated temporarily:", updatedTools);
    
    // Update local state
    setActiveTools(updatedTools);
    
    // Save to sessionStorage
    if (assistantId) {
      try {
        sessionStorage.setItem(`assistant_tools_${assistantId}`, JSON.stringify(updatedTools));
        //console.log("Saved tools to sessionStorage");
      } catch (error) {
        console.warn("Could not save tools to sessionStorage:", error);
      }
    }
  }, [assistantId]);

  useEffect(() => {
    if (assistantId) {
      try {
        const savedTools = sessionStorage.getItem(`assistant_tools_${assistantId}`);
        if (savedTools) {
          const parsedTools = JSON.parse(savedTools);
          //console.log("Loaded tools from session storage:", parsedTools);
          setActiveTools(parsedTools);
        }
      } catch (error) {
        console.warn("Could not restore tools from sessionStorage:", error);
      }
    }
  }, [assistantId]);

  // Extract user domain from current user for sharing settings
  const userDomain = useMemo(() => {
    if (currentUser?.email) {
      const parts = currentUser.email.split('@');
      return parts.length > 1 ? parts[1] : undefined;
    }
    return undefined;
  }, [currentUser]);

  const sharedProps = useMemo(() => ({
    userState,
    currentUser,
    showLoginDialog,
    setShowLoginDialog,
    handleLogout,
    adminEmail,
    initialDocuments,
    name,               
    avatar,            
    assistantId,        
    accessControl: localAccessControl,      
    senderName,
    initialMessage,
    initialInstructions,
    tools: activeTools,
    toolConfigs,
    onToolsChange: handleToolsChange,
    templateId          
  }), [
    userState,
    currentUser,
    showLoginDialog,
    handleLogout,
    adminEmail,
    initialDocuments,
    name,
    avatar,
    assistantId,
    localAccessControl,
    senderName,
    initialMessage,
    initialInstructions,
    activeTools,
    handleToolsChange,
    toolConfigs,
    templateId
  ]);

  const AccessDeniedContent = () => {
    //console.log("Rendering: AccessDeniedContent - Step 1 (Card Structure)");
    const message = getAccessDeniedMessage(localAccessControl, !!currentUser, adminEmail);
    return (
      <div className="min-h-screen w-full bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
        <Card className="max-w-xl w-full shadow-lg animate-fadeIn mx-auto">
          <CardHeader className="text-center space-y-6 pt-8">
          <div className="flex justify-center animate-scaleIn">
            <Avatar className="h-28 w-28 ring-4 ring-muted ring-offset-2 bg-destructive/10">
              <AvatarImage src={avatar} alt={name} />
              <AvatarFallback className="text-3xl">
                <Shield className="h-12 w-12 text-destructive" />
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="space-y-2 animate-slideUp">
            <h2 className="text-3xl font-bold text-foreground">Access Denied</h2>
            <p className="text-muted-foreground text-lg">Cannot access '{name}'</p>
          </div>
          </CardHeader>
          <CardContent className="space-y-6 px-8 pb-8">
          <div className="text-center space-y-6 animate-slideUp delay-300"> {/* Outer content div */}
            <div className="relative bg-primary/5 rounded-2xl p-6 max-w-md mx-auto"> {/* Message box */}
              <div className="absolute left-6 top-6">
                <Shield className="w-6 h-6 text-destructive shrink-0" />
              </div>
              <div className="text-lg text-foreground pl-10 text-left">
                <p>{message}</p> {/* Display the calculated message */}
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4"> {/* Button container */}
              <Button size="lg" variant="outline" asChild>
                <Link href="/">
                  <Home className="mr-2 h-5 w-5" /> Go Home
                </Link>
              </Button>
            </div>
          </div>
          </CardContent>
        </Card>
      </div>
    );
  };
  
  if (hasAccess === null) {
     //console.log("Render: Showing Loading Indicator (hasAccess is null)");

     // Show chat interface skeleton with available data
     return (
       <div className="flex min-h-screen bg-gray-50">
         <div className="flex-1 flex flex-col">
           <main className="flex-1 overflow-hidden w-full">
             <div className="w-full h-full overflow-auto flex flex-col p-0 md:p-4 md:max-w-[90vw] xl:max-w-[95vw] 2xl:max-w-[1800px] md:mx-auto md:px-4 bg-white rounded-lg shadow-sm border border-gray-200">
               {/* Header skeleton */}
               <div className="flex flex-wrap items-center justify-between p-4 border-b bg-white">
                 <div className="flex items-center gap-2 flex-1">
                   <div className="h-8 w-8 rounded bg-muted animate-pulse" />
                   <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold leading-tight tracking-tighter">
                     {name || 'Loading...'}
                   </h1>
                 </div>
               </div>

               {/* Chat interface skeleton */}
               <div className="flex-1 flex items-center justify-center p-8">
                 <div className="text-center space-y-6 max-w-md animate-fadeIn">
                   {/* Avatar */}
                   <div className="flex justify-center">
                     <div className="relative">
                       <Avatar className="h-24 w-24 ring-4 ring-primary/20">
                         <AvatarImage src={avatar} alt={name} />
                         <AvatarFallback className="text-2xl bg-primary/10">
                           {name?.slice(0, 2).toUpperCase() || 'AI'}
                         </AvatarFallback>
                       </Avatar>
                       <div className="absolute -bottom-1 -right-1">
                         <div className="h-8 w-8 rounded-full border-4 border-white bg-primary flex items-center justify-center">
                           <div className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                         </div>
                       </div>
                     </div>
                   </div>

                   {/* Loading text */}
                   <div className="space-y-2">
                     <h2 className="text-xl font-semibold text-foreground">
                       Preparing {name}...
                     </h2>
                     <p className="text-sm text-muted-foreground animate-pulse">
                       Setting up your conversation
                     </p>
                   </div>

                   {/* Message skeleton */}
                   <div className="space-y-3 text-left">
                     <div className="h-4 bg-muted rounded animate-pulse" />
                     <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                     <div className="h-4 bg-muted rounded animate-pulse w-5/6" />
                   </div>
                 </div>
               </div>
             </div>
           </main>
         </div>
       </div>
     );
  }
  if (hasAccess === false) {
    //console.log("Render: Showing Access Denied");
    return (
      <main className="flex-1">
        <AccessDeniedContent />
      {/* --- Temporarily Comment Out LoginDialog ---
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
      />
      */}
      </main>
    );
  }

  // Main chat interface
  return (
    <ArtifactProvider assistantId={assistantId}>
      <TooltipProvider delayDuration={100}>
          <div className="flex min-h-screen bg-gray-50">
      <div className="fixed inset-0 z-0 w-full h-full pointer-events-none">
            <Aurora
              colorStops={["#FF9966", "#FFD93D", "#FF6B6B"]}
              blend={0.9}                // Medium blend for better visibility
              amplitude={0.8}            // Higher amplitude for more noticeable waves
              speed={0.3}                // Slow animation speed
            />
            </div>
        <SidebarProvider defaultOpen={false}>
          <div className="flex flex-1">
            <AppSidebar
              botName={name}      
              currentBotAvatar={avatar} 
              senderName={senderName}
              userState={userState}
              currentUser={currentUser}
              documents={initialDocuments}
              onShowLogin={() => setShowLoginDialog(true)}
              onLogout={handleLogout}
              tools={tools}
              toolConfigs={toolConfigs}
              shareId={assistantId}  
              isAdminPage={false}    
              onToolsChange={handleToolsChange}
              accessControl={localAccessControl}
              ownerEmail={adminEmail}
              emailSubscriptions={config.emailSubscriptions}
            />
  
            <div className="flex-1 flex flex-col">
              {/* Move both the header and chat into a single scrollable container */}
              <main className="flex-1 overflow-hidden w-full">
                <div className="w-full h-full overflow-auto flex flex-col p-0 md:p-4 md:max-w-[90vw] xl:max-w-[95vw] 2xl:max-w-[1800px] md:mx-auto md:px-4 bg-white rounded-lg shadow-sm border border-gray-200">
                  {/* Header is now inside scrollable area */}
                  <div className="flex flex-wrap items-center justify-between p-4 border-b bg-white">
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                      <SidebarTrigger className="mr-2" />

                      {/* Parent Chat Link - shows if this is a duplicate */}
                      {parentAssistantId && (
                        <ParentChatLink parentAssistantId={parentAssistantId} />
                      )}

                      {/* Provenance Visualization Link */}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <Link href={`/assistant/${assistantId}/provenance`}>
                              <GitBranch className="h-4 w-4" />
                              <span className="hidden sm:inline ml-2">Lineage</span>
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View conversation lineage</p>
                        </TooltipContent>
                      </Tooltip>

                      <h1 className="text-2xl xs:text-3xl sm:text-4xl font-bold leading-tight tracking-tighter">
                        {name || 'Messages'}
                      </h1>
                      {/* Sharing status display */}
                      <SharingStatus 
                        accessControl={localAccessControl} 
                        userDomain={userDomain}
                        isEditable={userState === 'admin'} 
                        onAccessChange={handleAccessChange}
                        assistantId={assistantId}
                        assistantName={name}
                        className="ml-2 sm:ml-4 mt-1 sm:mt-0" 
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Artifact Toggle Button */}
                      <ArtifactToggleButton tools={activeTools} />

                      {/* Assistant Switcher with New Chat */}
                      <AssistantSwitcher
                        currentAssistantId={assistantId}
                        currentAssistantName={name}
                        currentAssistantAvatar={avatar}
                        currentUser={currentUser}
                        onNewChat={async () => {
                          // Show login dialog if user isn't logged in
                          if (!currentUser) {
                            setShowLoginDialog(true);
                            return;
                          }
                          try {
                            // Any authenticated user can create a new assistant
                            // Create duplicate config with current user as owner
                            const duplicateData = createDuplicateConfig({
                              assistantId,
                              templateId,
                              name,
                              avatar,
                              ownerEmail: currentUser.email || '',
                              initialMessage,  // Copy the greeting from parent
                              initialInstructions,
                              tools: activeTools,
                              toolConfigs,
                              initialDocuments: initialDocuments || [],
                              // Set to private by default instead of copying the original access control
                              accessControl: { type: 'private' },
                              emailSubscriptions: [] // Always empty for duplicates
                            } as any, currentUser.uid, currentUser.email || '');

                            // Create assistant with provenance tracking
                            const newAssistantId = await FirebaseService.createAssistant(
                              duplicateData,
                              {
                                parentAssistantId: assistantId,  // Link to parent
                                copyFirstMessage: true            // Copy greeting with parent link
                              }
                            );

                            if (newAssistantId) {
                              // Store current assistant data for loading screen
                              try {
                                sessionStorage.setItem('pendingAssistantLoad', JSON.stringify({
                                  name,
                                  avatar,
                                  timestamp: Date.now()
                                }));
                              } catch (e) {
                                console.warn('Could not store assistant data:', e);
                              }

                              // Redirect to the new assistant
                              window.location.href = `/assistant/${newAssistantId}`;
                            }
                          } catch (error) {
                            console.error('Error duplicating assistant:', error);
                            alert('Failed to duplicate assistant. Please try again.');
                            throw error; // Re-throw so AssistantSwitcher can reset loading state
                          }
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Chat and Artifact Layout - conditional side by side */}
                  <ChatWithArtifacts
                    sharedProps={sharedProps}
                    currentUser={currentUser}
                    showLoginDialog={showLoginDialog}
                    setShowLoginDialog={setShowLoginDialog}
                  />
                </div>
              </main>
            </div>
          </div>
  
          <LoginDialog
            open={showLoginDialog}
            onOpenChange={setShowLoginDialog}
          />
        </SidebarProvider>
          </div>
        </TooltipProvider>
      </ArtifactProvider>
  );
}